import type { CampaignAnalyticsGranularity, CampaignAnalyticsResponse } from "@adpilot/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PerformancePanel,
  PerformancePanelEmptyState,
} from "@/components/shared/performance/PerformancePanel";
import {
  DEFAULT_PERFORMANCE_SLOTS,
  PERFORMANCE_METRICS,
  type PerformanceMetric,
  type PerformanceTrendMarkers,
} from "@/components/shared/performance/performance";
import type { ReportingTimeZone } from "@/lib/reporting-time-zone";
import type { ReportingPeriod } from "@/stores/reporting-period";
import { campaignDetailPerformanceQuery } from "./campaign-detail-performance-query";

interface CampaignDetailPerformanceProps {
  campaignId: string;
  shopId: string | null;
  range: ReportingPeriod;
  timeZone: ReportingTimeZone;
  markers?: PerformanceTrendMarkers;
  /** Lets a tab keep its own expanded/collapsed choice. */
  collapseStorageKey?: string;
}

export function CampaignDetailPerformance({
  campaignId,
  shopId,
  range,
  timeZone,
  markers,
  collapseStorageKey,
}: CampaignDetailPerformanceProps) {
  const [granularity, setGranularity] = useState<CampaignAnalyticsGranularity>("day");
  const query = useQuery(
    campaignDetailPerformanceQuery({ campaignId, shopId, range, timeZone, granularity }),
  );
  return (
    <CampaignDetailPerformanceView
      data={query.data}
      isLoading={!!shopId && query.isPending}
      isFetching={query.isFetching}
      isError={query.isError}
      onRetry={() => void query.refetch()}
      granularity={granularity}
      onGranularityChange={setGranularity}
      markers={markers}
      collapseStorageKey={collapseStorageKey}
    />
  );
}

export interface CampaignDetailPerformanceViewProps {
  data?: CampaignAnalyticsResponse;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  granularity: CampaignAnalyticsGranularity;
  onGranularityChange: (value: CampaignAnalyticsGranularity) => void;
  onRetry: () => void;
  markers?: PerformanceTrendMarkers;
  collapseStorageKey?: string;
}

export function CampaignDetailPerformanceView(props: CampaignDetailPerformanceViewProps) {
  const { t } = useTranslation("campaign");
  const [metric, setMetric] = useState<PerformanceMetric>("spend");
  const [compareMetric, setCompareMetric] = useState<PerformanceMetric | null>("sales");
  const emptyState =
    props.data && (!props.data.connected || props.data.matchedCampaigns === 0) ? (
      <PerformancePanelEmptyState
        title={t("performance.noDataTitle")}
        description={t("detail.performance.noCampaignData")}
      />
    ) : undefined;
  return (
    <div data-slot="campaign-detail-performance">
      <PerformancePanel
        showPreviousValue
        metrics={PERFORMANCE_METRICS}
        defaultSlots={DEFAULT_PERFORMANCE_SLOTS}
        kpis={(props.data?.kpis ?? []).map((kpi) => ({
          ...kpi,
          currency: props.data?.currency ?? null,
        }))}
        trend={props.data?.trend ?? []}
        dataThrough={props.data?.dataThrough ?? null}
        metric={metric}
        onMetricChange={setMetric}
        compareMetric={compareMetric}
        onCompareMetricChange={setCompareMetric}
        granularity={props.granularity}
        onGranularityChange={props.onGranularityChange}
        isLoading={props.isLoading}
        isFetching={props.isFetching}
        isError={props.isError}
        onRetry={props.onRetry}
        title={t("detail.performance.title")}
        getMetricLabel={(key) => t(`detail.performance.metrics.${key}`)}
        collapseStorageKey={props.collapseStorageKey ?? "adpilot:campaign-detail-performance:v1"}
        slotsStorageKey="adpilot:campaign-detail-performance-slots:v1"
        emptyState={emptyState}
        markers={props.markers}
      />
    </div>
  );
}
